import _toConsumableArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
var __jsx = React.createElement;
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { updateCart, checkoutCart, getCart } from "@/apis/oils/cartApi";
import FullPageContext from "@/common/Contexts/FullPageContext";
var CartContext = /*#__PURE__*/createContext(undefined);
var CART_ID_KEY = "cart_id";
var toApiItems = function toApiItems(items) {
  return items.map(function (i) {
    return {
      type: i.type,
      id: i.id,
      count: i.count
    };
  });
};

// Rebuild local ICartItem[] from getCart response (for badge counts after migration)
var itemsFromCartData = function itemsFromCartData(data) {
  var block = data.find(function (b) {
    return b.component_type === "cart_items";
  });
  if (!block) return [];
  return block.content.filter(function (b) {
    return b.component_type === "cart_item_oil";
  }).map(function (b) {
    return {
      id: b.content.id,
      type: "coupon",
      count: b.content.count,
      price: b.content.price
    };
  });
};

// Pull the cart blocks out of an SSR full-page payload (the oil pages embed
// cart_items / cart_summary / cart_cta blocks alongside their content).
var ssrCartBlocks = function ssrCartBlocks(data) {
  if (!Array.isArray(data)) return null;
  var blocks = data.filter(function (b) {
    return b.component_type === "cart_items" || b.component_type === "cart_summary" || b.component_type === "cart_cta";
  });
  return blocks.length ? blocks : null;
};
var cartIdFromBlocks = function cartIdFromBlocks(blocks) {
  var _header$cart_id;
  var itemsBlock = blocks === null || blocks === void 0 ? void 0 : blocks.find(function (b) {
    return b.component_type === "cart_items";
  });
  var header = itemsBlock === null || itemsBlock === void 0 ? void 0 : itemsBlock.content.find(function (b) {
    return b.component_type === "cart_header";
  });
  return (_header$cart_id = header === null || header === void 0 ? void 0 : header.cart_id) !== null && _header$cart_id !== void 0 ? _header$cart_id : null;
};
export var CartProvider = function CartProvider(_ref) {
  var _ctaBlock$content$pri, _ctaBlock$content, _ctaBlock$content$pri2;
  var children = _ref.children;
  // Feed the context from the SSR full-page payload so the cart badge is correct
  // on the very first paint (landing/refresh) instead of waiting for a client
  // effect to seed it. The server reads it from FullPageContext (which wraps
  // <App>); the client reads the same payload from the injected global (there is
  // no FullPageContext.Provider on the client). Same data both sides => the
  // first render matches and there is no hydration mismatch.
  var fullPageDataFromContext = useContext(FullPageContext);
  var ssrFullPageData = __IS_CSR__ ? typeof window !== "undefined" ? window.FULL_PAGE_DATA : null : fullPageDataFromContext;
  var ssrBlocks = ssrCartBlocks(ssrFullPageData);
  var _useState = useState(function () {
      return ssrBlocks ? itemsFromCartData(ssrBlocks) : [];
    }),
    items = _useState[0],
    setItems = _useState[1];
  var _useState2 = useState(function () {
      return cartIdFromBlocks(ssrBlocks);
    }),
    cartId = _useState2[0],
    setCartId = _useState2[1];
  var _useState3 = useState(function () {
      return ssrBlocks;
    }),
    cartData = _useState3[0],
    setCartData = _useState3[1];
  var _useState4 = useState(false),
    isLoading = _useState4[0],
    setIsLoading = _useState4[1];
  // True from the instant a +/- click schedules a sync until the server
  // confirms it. The "Add More" button is disabled while this is true so a hard
  // navigation can't out-race the debounced write and land on a stale listing.
  var _useState5 = useState(false),
    isSyncing = _useState5[0],
    setIsSyncing = _useState5[1];
  var _useState6 = useState(0),
    cartCountFromMeta = _useState6[0],
    setCartCountFromMeta = _useState6[1];

  // Debounced server sync + out-of-order response guard.
  // Rapid +/- clicks update `items` optimistically and collapse into a single
  // server call; only the latest response is allowed to reconcile state, so a
  // stale response can't snap the quantity back to an old value.
  var syncTimer = useRef(null);
  var syncSeq = useRef(0);
  var pendingItemsRef = useRef(null);
  var cartIdRef = useRef(null);
  var SYNC_DELAY_MS = 350;
  useEffect(function () {
    cartIdRef.current = cartId;
  }, [cartId]);
  useEffect(function () {
    if (typeof window === "undefined") return;
    var match = document.cookie.split(";").map(function (c) {
      return c.trim();
    }).find(function (c) {
      return c.startsWith("".concat(CART_ID_KEY, "="));
    });
    var cookieId = match ? decodeURIComponent(match.slice(CART_ID_KEY.length + 1)) : null;
    if (cookieId) {
      setCartId(cookieId);
      cartIdRef.current = cookieId;
    }
  }, []);
  var persistLocal = useCallback(function (nextCartId) {
    if (typeof window === "undefined") return;
    if (nextCartId) {
      document.cookie = "".concat(CART_ID_KEY, "=").concat(nextCartId, "; path=/; max-age=2592000; SameSite=Lax");
    } else {
      document.cookie = "".concat(CART_ID_KEY, "=; path=/; max-age=0");
    }
  }, []);

  // Fire the actual network request. Guarded by a sequence number so that an
  // out-of-order (stale) response never clobbers a newer optimistic state.
  var flushSync = useCallback(/*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(nextItems) {
      var _header$cart_id2;
      var seq, currentCartId, data, itemsBlock, header, reconciledId, reconciledItems;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.n) {
          case 0:
            seq = ++syncSeq.current;
            currentCartId = cartIdRef.current;
            setIsLoading(true);
            _context.n = 1;
            return updateCart(currentCartId, toApiItems(nextItems));
          case 1:
            data = _context.v;
            if (!(seq !== syncSeq.current)) {
              _context.n = 2;
              break;
            }
            return _context.a(2);
          case 2:
            if (data) {
              _context.n = 3;
              break;
            }
            setIsLoading(false);
            setIsSyncing(false);
            return _context.a(2);
          case 3:
            // Extract cart_id from the cart_header block inside cart_items
            itemsBlock = data.find(function (b) {
              return b.component_type === "cart_items";
            });
            header = itemsBlock === null || itemsBlock === void 0 ? void 0 : itemsBlock.content.find(function (b) {
              return b.component_type === "cart_header";
            });
            reconciledId = (_header$cart_id2 = header === null || header === void 0 ? void 0 : header.cart_id) !== null && _header$cart_id2 !== void 0 ? _header$cart_id2 : currentCartId;
            reconciledItems = itemsFromCartData(data);
            if (reconciledId) {
              setCartId(reconciledId);
              cartIdRef.current = reconciledId;
            }
            setItems(reconciledItems);
            setCartData(data);
            persistLocal(reconciledId);
            setIsLoading(false);
            setIsSyncing(false);
          case 4:
            return _context.a(2);
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [persistLocal]);

  // Debounce rapid +/- clicks into a single server call. The latest item
  // snapshot wins; UI already reflects the optimistic state immediately.
  var syncToServer = useCallback(function (nextItems) {
    setIsSyncing(true);
    pendingItemsRef.current = nextItems;
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(function () {
      syncTimer.current = null;
      var items = pendingItemsRef.current;
      pendingItemsRef.current = null;
      if (items) flushSync(items);
    }, SYNC_DELAY_MS);
  }, [flushSync]);

  // Flush any pending sync if the page is being unloaded (e.g. navigating to
  // the cart) so the server isn't left with a stale snapshot.
  useEffect(function () {
    if (typeof window === "undefined") return;
    var flushNow = function flushNow() {
      if (syncTimer.current) {
        clearTimeout(syncTimer.current);
        syncTimer.current = null;
      }
      var items = pendingItemsRef.current;
      pendingItemsRef.current = null;
      if (items) flushSync(items);
    };
    window.addEventListener("pagehide", flushNow);
    return function () {
      return window.removeEventListener("pagehide", flushNow);
    };
  }, [flushSync]);
  var loadCart = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
    var _document$cookie$spli, _document$cookie$spli2;
    var cookieId, id, data;
    return _regenerator().w(function (_context2) {
      while (1) switch (_context2.p = _context2.n) {
        case 0:
          cookieId = typeof window !== "undefined" ? (_document$cookie$spli = (_document$cookie$spli2 = document.cookie.split(";").map(function (c) {
            return c.trim();
          }).find(function (c) {
            return c.startsWith("".concat(CART_ID_KEY, "="));
          })) === null || _document$cookie$spli2 === void 0 ? void 0 : _document$cookie$spli2.slice(CART_ID_KEY.length + 1)) !== null && _document$cookie$spli !== void 0 ? _document$cookie$spli : null : null;
          id = cookieId || cartId;
          setIsLoading(true);
          _context2.p = 1;
          _context2.n = 2;
          return getCart(id !== null && id !== void 0 ? id : null);
        case 2:
          data = _context2.v;
          if (data) {
            setCartData(data);
            // Live cart is authoritative: replace local items (even when empty) so
            // the badge and per-option counts reflect the real server state.
            setItems(itemsFromCartData(data));
          } else {
            setCartData([]);
          }
        case 3:
          _context2.p = 3;
          setIsLoading(false);
          return _context2.f(3);
        case 4:
          return _context2.a(2);
      }
    }, _callee2, null, [[1,, 3, 4]]);
  })), [cartId]);
  var addItem = useCallback(function (item) {
    setItems(function (prev) {
      var existing = prev.find(function (i) {
        return i.id === item.id;
      });
      var next = existing ? prev.map(function (i) {
        return i.id === item.id ? _objectSpread(_objectSpread({}, i), {}, {
          count: i.count + 1
        }) : i;
      }) : [].concat(_toConsumableArray(prev), [_objectSpread(_objectSpread({}, item), {}, {
        count: 1
      })]);
      syncToServer(next);
      return next;
    });
  }, [syncToServer]);
  var removeItem = useCallback(function (id) {
    setItems(function (prev) {
      var next = prev.filter(function (i) {
        return i.id !== id;
      });
      syncToServer(next);
      return next;
    });
  }, [syncToServer]);
  var updateQuantity = useCallback(function (id, count) {
    if (count <= 0) {
      removeItem(id);
      return;
    }
    setItems(function (prev) {
      var next = prev.map(function (i) {
        return i.id === id ? _objectSpread(_objectSpread({}, i), {}, {
          count: count
        }) : i;
      });
      syncToServer(next);
      return next;
    });
  }, [removeItem, syncToServer]);
  var clearCart = useCallback(function () {
    if (syncTimer.current) {
      clearTimeout(syncTimer.current);
      syncTimer.current = null;
    }
    pendingItemsRef.current = null;
    syncSeq.current++; // invalidate any in-flight sync response
    var prevCartId = cartIdRef.current;
    setItems([]);
    setCartId(null);
    cartIdRef.current = null;
    setCartData([]);
    persistLocal(null);
    updateCart(prevCartId, []).then(function (d) {
      if (d) setCartData(d);
    });
  }, [persistLocal]);
  var getItemCount = useCallback(function (id) {
    var _items$find$count, _items$find;
    return (_items$find$count = (_items$find = items.find(function (i) {
      return i.id === id;
    })) === null || _items$find === void 0 ? void 0 : _items$find.count) !== null && _items$find$count !== void 0 ? _items$find$count : 0;
  }, [items]);
  var seedCartMeta = useCallback(function (newCartId, count) {
    if (newCartId) {
      setCartId(newCartId);
      cartIdRef.current = newCartId;
      persistLocal(newCartId);
    }
    setCartCountFromMeta(count);
  }, [persistLocal]);
  var seedCart = useCallback(function (blocks) {
    var _header$cart_id3;
    var itemsBlock = blocks.find(function (b) {
      return b.component_type === "cart_items";
    });
    var header = itemsBlock === null || itemsBlock === void 0 ? void 0 : itemsBlock.content.find(function (b) {
      return b.component_type === "cart_header";
    });
    var newCartId = (_header$cart_id3 = header === null || header === void 0 ? void 0 : header.cart_id) !== null && _header$cart_id3 !== void 0 ? _header$cart_id3 : null;
    setCartData(blocks);
    var synced = itemsFromCartData(blocks);
    if (synced.length > 0) setItems(synced);
    if (newCartId) {
      setCartId(newCartId);
      cartIdRef.current = newCartId;
      persistLocal(newCartId);
    }
  }, [persistLocal]);
  var checkout = useCallback(/*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee3(successUrl, failureUrl) {
      var _document$cookie$spli3, _document$cookie$spli4;
      var cookieId, id;
      return _regenerator().w(function (_context3) {
        while (1) switch (_context3.p = _context3.n) {
          case 0:
            cookieId = typeof window !== "undefined" ? (_document$cookie$spli3 = (_document$cookie$spli4 = document.cookie.split(";").map(function (c) {
              return c.trim();
            }).find(function (c) {
              return c.startsWith("".concat(CART_ID_KEY, "="));
            })) === null || _document$cookie$spli4 === void 0 ? void 0 : _document$cookie$spli4.slice(CART_ID_KEY.length + 1)) !== null && _document$cookie$spli3 !== void 0 ? _document$cookie$spli3 : null : null;
            id = cookieId || cartId;
            setIsLoading(true);
            _context3.p = 1;
            _context3.n = 2;
            return checkoutCart(id, successUrl, failureUrl);
          case 2:
            return _context3.a(2, _context3.v);
          case 3:
            _context3.p = 3;
            setIsLoading(false);
            return _context3.f(3);
          case 4:
            return _context3.a(2);
        }
      }, _callee3, null, [[1,, 3, 4]]);
    }));
    return function (_x2, _x3) {
      return _ref4.apply(this, arguments);
    };
  }(), [cartId]);
  var totalItems = items.length > 0 ? items.reduce(function (sum, i) {
    return sum + i.count;
  }, 0) : cartCountFromMeta;
  var totalCurrentPrice = items.reduce(function (sum, i) {
    return sum + i.price.current * i.count;
  }, 0);
  var totalOriginalPrice = items.reduce(function (sum, i) {
    return sum + i.price.original * i.count;
  }, 0);
  var ctaBlock = cartData === null || cartData === void 0 ? void 0 : cartData.find(function (b) {
    return b.component_type === "cart_cta";
  });
  var totalFinalPrice = (_ctaBlock$content$pri = ctaBlock === null || ctaBlock === void 0 ? void 0 : (_ctaBlock$content = ctaBlock.content) === null || _ctaBlock$content === void 0 ? void 0 : (_ctaBlock$content$pri2 = _ctaBlock$content.price) === null || _ctaBlock$content$pri2 === void 0 ? void 0 : _ctaBlock$content$pri2.current) !== null && _ctaBlock$content$pri !== void 0 ? _ctaBlock$content$pri : totalCurrentPrice;
  return /*#__PURE__*/React.createElement(CartContext.Provider, {
    value: {
      items: items,
      cartId: cartId,
      cartData: cartData,
      totalItems: totalItems,
      totalCurrentPrice: totalCurrentPrice,
      totalOriginalPrice: totalOriginalPrice,
      isLoading: isLoading,
      isSyncing: isSyncing,
      totalFinalPrice: totalFinalPrice,
      addItem: addItem,
      removeItem: removeItem,
      updateQuantity: updateQuantity,
      clearCart: clearCart,
      getItemCount: getItemCount,
      loadCart: loadCart,
      seedCart: seedCart,
      seedCartMeta: seedCartMeta,
      checkout: checkout
    }
  }, children);
};
export var useCart = function useCart() {
  var ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};